import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdOrdersOrderIdInvoices,
  GetV1CompaniesCompanyIdOrdersOrderIdInvoicesData,
  InvoiceableType,
  InvoiceCategoryFilter,
  InvoiceState,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { noop, uniqBy } from 'lodash-es';
import { createContext, useCallback, useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Invoice } from '~hooks/useInvoices/models';
import { Order } from '~hooks/useOrders/models';
import { makeInvoicesQueryContextKey } from '~pages/Approvals/DriverPayV2/utils';
import { Pagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type InvoicesQueryFilters = {
  startDate?: Dayjs;
  endDate?: Dayjs;
  invoiceCategory?: InvoiceCategoryFilter;
  invoiceStates?: InvoiceState[];
  grouped?: boolean;
  rateTypes?: RateType[];
  projectIds?: string[];
  vendorAccountIds?: string[];
  equipmentOwnerCompanyIds?: string[];
  customerAccountIds?: string[];
  pickupSiteIds?: string[];
  dropoffSiteIds?: string[];
  driverIds?: string[];
  companyId?: string;
  searchQuery?: string;
};

type InvoicesQueryContextType = {
  data?: Invoice[];
  fetch: () => void;
  order: Order;
  updateInvoice: (invoice: Invoice) => void;
  filters: InvoicesQueryFilters;
  applyFilters: (filters: InvoicesQueryFilters) => void;
  applySearchQuery: (searchQuery: string) => void;
};

const InvoicesQueryContext = createContext<InvoicesQueryContextType>({
  fetch: noop,
  order: {} as Order,
  updateInvoice: noop,
  filters: {},
  applyFilters: noop,
  applySearchQuery: noop,
});

interface InvoicesQueryContextProviderProps {
  order: Order;
  category: InvoiceCategoryFilter;
  searchQuery?: string;
}

type InvoicesQueryParams = {
  filters: InvoicesQueryFilters;
  pagination?: Pagination;
};
function InvoicesQueryContextProvider({
  children,
  order,
  category,
  searchQuery,
}: React.PropsWithChildren<InvoicesQueryContextProviderProps>) {
  const queryKey = makeInvoicesQueryContextKey(order.id, category);
  const queryClient = useQueryClient();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [shouldFetch, setShouldFetch] = useState(false);

  const [queryParams, setQueryParams] = useState<InvoicesQueryParams>({
    filters: {},
  });
  const query = useQuery({
    queryKey: [queryKey],
    queryFn: shouldFetch
      ? async ({ signal }) => {
          const { filters } = queryParams;

          const query: GetV1CompaniesCompanyIdOrdersOrderIdInvoicesData['query'] = {
            'page[limit]': MAX_API_PAGE_SIZE,
            'filter[invoice_category]': filters.invoiceCategory ?? category,
            'filter[states]': filters.invoiceStates ?? undefined,
            'filter[grouped]': filters.grouped ?? undefined,
            'filter[rate_types]': filters.rateTypes ?? undefined,
            'filter[project_ids]': filters.projectIds ?? undefined,
            'filter[vendor_account_ids]': filters.vendorAccountIds ?? undefined,
            'filter[equipment][company_ids]':
              filters.equipmentOwnerCompanyIds ?? undefined,
            'filter[customer_account_ids]': filters.customerAccountIds ?? undefined,
            'filter[pickup_site_ids]': filters.pickupSiteIds ?? undefined,
            'filter[dropoff_site_ids]': filters.dropoffSiteIds ?? undefined,
            'filter[driver_ids]': filters.driverIds ?? undefined,
            'filter[start_date]': filters.startDate
              ? dayjs.tz(filters.startDate).startOf('day').toISOString()
              : undefined,
            'filter[end_date]': filters.endDate
              ? dayjs.tz(filters.endDate).endOf('day').toISOString()
              : undefined,
            'filter[invoiceable_type]': InvoiceableType.JOB,
            'search[datagrid]': searchQuery ?? filters.searchQuery ?? undefined,
          };

          const response = await getV1CompaniesCompanyIdOrdersOrderIdInvoices({
            path: { 'company-id': companyId, 'order-id': order.id },
            query,
            signal,
          });

          const invoices = response.data.data.map((invoice) => Invoice.parse(invoice));

          return invoices;
        }
      : skipToken,
  });

  const fetch = useCallback(() => {
    setShouldFetch(true);
  }, []);

  const updateInvoice = useCallback(
    (invoice: Invoice) => {
      queryClient.setQueryData([queryKey], (oldInvoices: Invoice[]) => {
        const oldInvoiceIndex = oldInvoices.findIndex((i) => i.id === invoice.id);

        if (oldInvoiceIndex >= 0) {
          const newInvoices = [...oldInvoices];
          newInvoices.splice(oldInvoiceIndex, 1, invoice);

          return newInvoices;
        }

        return oldInvoices;
      });
    },
    [queryKey],
  );

  const clear = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return keyAsString.startsWith(queryKey);
        });
      },
    });
  }, [queryKey]);

  const applyFilters = useCallback<InvoicesQueryContextType['applyFilters']>(
    (filters) => {
      clear();
      setQueryParams((state) => ({
        filters: { ...state.filters, ...filters },
      }));
      setShouldFetch(true);
    },
    [clear],
  );

  const applySearchQuery = useCallback<InvoicesQueryContextType['applySearchQuery']>(
    (searchQuery) => {
      clear();
      setQueryParams((state) => ({
        filters: { ...state.filters, searchQuery: searchQuery || undefined },
      }));
      setShouldFetch(true);
    },
    [clear],
  );

  return (
    <InvoicesQueryContext.Provider
      value={{
        data: query.data ? uniqBy(query.data, (invoice) => invoice.id) : undefined,
        fetch,
        order,
        updateInvoice,
        filters: queryParams.filters,
        applyFilters,
        applySearchQuery,
      }}
    >
      {children}
    </InvoicesQueryContext.Provider>
  );
}

export default InvoicesQueryContext;
export { InvoicesQueryContextProvider };
